import { useMemo } from 'react';
import { Link } from 'react-router';
import API from '../../../../Utils/api';
import useAsync from '../../../../Hooks/useAsync';
import Card from '../../../../Components/Dashboard/Card';
import Pill from '../../../../Components/Dashboard/Pill';
import { CardEmpty, CardError, CardLoading } from '../../../../Components/Dashboard/CardState';
import { addLocalDays, startOfLocalDay } from '../../../../Utils/dates/localDay';
import { formatShortDate, formatThousands, formatTime } from '../../../../Utils/dates/formatDate';
import NextWorkoutSets from './NextWorkoutSets';
import UpcomingList from './UpcomingList';
import styles from '../dashboardCards.module.css';

const TITLE = 'Next workout';
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const dayWord = (date, now) => {
    const diff = Math.round((startOfLocalDay(date) - startOfLocalDay(now)) / 86400000);
    if (diff === 0) return 'today';
    if (diff === 1) return 'tomorrow';
    return formatShortDate(date);
};

/** Read only: the next session, its sets and zone mix, and the three after it. */
const NextWorkoutCard = ({ athleteId, now: nowProp, calendarPath = '/calendar' }) => {
    const now = useMemo(() => nowProp || new Date(), [nowProp]);
    const { status, data, error, reload } = useAsync(
        () => API.getUpcomingWorkouts(athleteId, { from: startOfLocalDay(now), limit: 4 }).then(res => res.data),
        [athleteId]
    );

    let body;
    if (status === 'loading' && !data) body = <CardLoading label={TITLE} />;
    else if (status === 'error') body = <CardError label={TITLE} status={error?.response?.status} onRetry={reload} />;
    else if (data && data.length === 0) {
        body = <CardEmpty>Nothing scheduled. Your coach hasn't assigned a session yet.</CardEmpty>;
    } else if (data) {
        const [next, ...rest] = data;
        const start = new Date(next.workoutStart);
        body = (
            <>
                <div className={styles.nwHead}>
                    <div className={styles.nwDate} aria-hidden="true">
                        <span>{MONTH[start.getMonth()]}</span>
                        <b>{start.getDate()}</b>
                    </div>
                    <div className={styles.nwTitle}>
                        <h3>{next.title || 'Workout'}</h3>
                        <div className={styles.facts} role="group" aria-label="Session facts">
                            <span>{DOW[start.getDay()]}</span>
                            <span>{`${dayWord(start, now)} ${formatTime(start)}`}</span>
                            {next.volume != null && <b>{`${formatThousands(next.volume)} m`}</b>}
                            {next.trainingDurationMin != null && <b>{`~${next.trainingDurationMin} min`}</b>}
                            {next.assignedBy && (
                                <span>{`Assigned by ${next.assignedBy.firstName} ${next.assignedBy.lastName}`}</span>
                            )}
                            <Pill tone="info">{next.status}</Pill>
                        </div>
                    </div>
                    <div className={styles.actions}>
                        <Link className={styles.btn} to={calendarPath}>Open calendar</Link>
                        <Link className={`${styles.btn} ${styles.primary}`} to={calendarPath}>View workout</Link>
                    </div>
                </div>
                <div className={styles.split}>
                    <div className={styles.pane}>
                        <NextWorkoutSets items={next.items} />
                    </div>
                    <div>
                        <div className={styles.pane} style={{ paddingBottom: 6 }}>
                            <div className={styles.subhead}>
                                <span>Upcoming</span>
                                <span>Status</span>
                            </div>
                        </div>
                        {rest.length > 0 ? <UpcomingList rows={rest.slice(0, 3)} /> : <p className={`${styles.pane} ${styles.muted}`}>Nothing else scheduled yet.</p>}
                    </div>
                </div>
            </>
        );
    }

    return (
        <Card id="nw" module="athlete-next" title={TITLE} busy={status === 'loading'} flush>
            {body}
        </Card>
    );
};

export default NextWorkoutCard;
